import {
  Datagrid,
  FunctionField,
  List,
  SearchInput,
  SelectInput,
  TextInput,
  useGetList,
  useGetOne,
} from 'react-admin';
import { Avatar, Box, Typography } from '@mui/material';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';

import { useTitle } from '@src/hooks/useTitle';
import { getCurrencySymbol } from '@src/utils/currency';
import type { PartnerSettings } from '@src/settings';
import type { FacetGroup } from '@src/facets';

import { ResalePricingTabShell, useNotifyOnError } from '../shared';
import WithPricingGuard from './WithPricingGuard';
import ItemAdjustedPricesActions from './ItemAdjustedPricesActions';
import ItemAdjustedPricesExpand from './ItemAdjustedPricesExpand';
import OverrideProgressBanner from './OverrideProgressBanner';
import type { YAdjustedRowWithId } from './types';

const formatCents = (value: number | null | undefined, symbol: string): string =>
  (value == null ? '—' : `${symbol}${(value / 100).toFixed(2)}`);

const Money = ({ value, symbol, strong }: { value: number | null | undefined, symbol: string, strong?: boolean }) => (
  <Typography
    variant="body2"
    sx={{
      fontVariantNumeric: 'tabular-nums',
      fontWeight: strong ? 600 : 400,
      color: strong ? 'text.primary' : 'text.secondary',
    }}
  >
    {formatCents(value, symbol)}
  </Typography>
);

/**
 * Item price overrides ("Overrides" tab). Read-only rows uploaded as
 * markdowns. Filtering happens client-side in the data provider — the
 * yadjustedPricing API has no server-side filtering — so an active filter
 * pulls the full set and paginates the matches client-side.
 */
const ItemAdjustedPricesList = () => {
  const { title } = useTitle('Resale Pricing · Overrides');
  const { data: settings } = useGetOne<PartnerSettings & { id: number }>('settings', { id: 0 });
  const { data: facetGroups = [], error: facetsError } = useGetList<FacetGroup>('facets');
  const symbol = getCurrencySymbol(settings?.primaryCurrencyCode);

  // Distinguish a failed load from "no options configured" for the facet-backed
  // Department/Category filters below.
  useNotifyOnError(facetsError, "Couldn't load filter options — some filters may be unavailable.");

  const choicesFor = (tag: string) =>
    facetGroups.find(fg => fg.tag === tag)?.values.map(v => ({ id: v.name, name: v.name })) || [];

  const filters = [
    <SearchInput key="searchQuery" source="searchQuery" placeholder="Search SKU or title" alwaysOn />,
    <SelectInput key="department" source="department" label="Department" choices={choicesFor('department')} helperText={false} alwaysOn />,
    <SelectInput key="category" source="category" label="Category" choices={choicesFor('category')} helperText={false} alwaysOn />,
    <TextInput key="color" source="color" label="Color" helperText={false} />,
    <TextInput key="sizeCode" source="sizeCode" label="Size" helperText={false} />,
    <TextInput key="yadjustedCode" source="yadjustedCode" label="Markdown code" helperText={false} />,
  ];

  return (
    <ResalePricingTabShell tab="itemAdjustedPrices">
      <WithPricingGuard>
        <OverrideProgressBanner />
        <List
          perPage={25}
          title={title}
          filters={filters}
          actions={<ItemAdjustedPricesActions />}
          exporter={false}
          // Overrides are created by uploading markdowns (in the toolbar), not a
          // create form. Keep the toolbar visible when empty so there is always
          // a way to add the first override; react-admin's default empty page
          // would otherwise replace the toolbar and hide the upload button.
          empty={false}
        >
          <Datagrid
            bulkActionButtons={false}
            expand={<ItemAdjustedPricesExpand />}
            rowClick="expand"
            sx={{ '& .RaDatagrid-rowCell': { verticalAlign: 'middle' } }}
          >
            <FunctionField<YAdjustedRowWithId>
              label="Item"
              sortable={false}
              render={row => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <Avatar
                    variant="rounded"
                    sx={{ width: 36, height: 36, bgcolor: 'grey.100', color: 'grey.500', border: '1px solid', borderColor: 'divider' }}
                  >
                    <SellOutlinedIcon fontSize="small" />
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.title ?? '—'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: "'SFMono-Regular', Consolas, monospace" }}>
                      {[row.parentSku, row.skuSubcode].filter(Boolean).join(' · ') || '—'}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
            <FunctionField<YAdjustedRowWithId> label="Color" sortable={false} render={row => row.color ?? '—'} />
            <FunctionField<YAdjustedRowWithId> label="Size" sortable={false} render={row => row.sizeCode ?? '—'} />
            <FunctionField<YAdjustedRowWithId> label="Department" sortable={false} render={row => row.department ?? '—'} />
            <FunctionField<YAdjustedRowWithId> label="Category" sortable={false} render={row => row.category ?? '—'} />
            <FunctionField<YAdjustedRowWithId>
              label="Base price"
              sortable={false}
              textAlign="right"
              render={row => <Money value={row.price} symbol={symbol} />}
            />
            <FunctionField<YAdjustedRowWithId>
              label="Resale price"
              sortable={false}
              textAlign="right"
              render={row => <Money value={row.yerdlePrice} symbol={symbol} />}
            />
            <FunctionField<YAdjustedRowWithId>
              label="Override"
              sortable={false}
              textAlign="right"
              render={row => <Money value={row.yadjustedPrice} symbol={symbol} strong />}
            />
          </Datagrid>
        </List>
      </WithPricingGuard>
    </ResalePricingTabShell>
  );
};

export default ItemAdjustedPricesList;
